/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Route, Switch, useRouteMatch, Redirect} from 'react-router'
import {
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Flex,
    Grid,
    Stack,
    Text,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevronUp, ChevronDown} from '../../components/icons'
import AccountDetail from './profile'
import AccountAddresses from './addresses'

import Orders from './my-orders'
//import Orders from './my-orders/orders'
import MarketingPreferences from './marketing-preferences'

import AccountWishlist from './wishlist/index'
import RegisteredProducts from './registered-products'
import RepairTracking from './repair-tracking-tool'
import SubscriptionList from './subscription-history'
import SubscriptionDetails from './subscription-details'
import SubscriptionCancel from './subscription-cancel'
import {useLocation} from 'react-router-dom'

import {accountMessages, accountNavLinks} from './constant'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import PaymentMethods from './payment-methods/payment-methods'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import {BRAND_KEY} from '../../brand'
import {remCalc} from '../../theme/_utils'
import GenericHtmlPage from '../generic-html-page'

const onClient = typeof window !== 'undefined'
const Account = () => {
    const {path} = useRouteMatch()
    const {formatMessage} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const {isRegistered, customerType} = customer
    const styles = useMultiStyleConfig('Account')
    const hideAccountPref = useBooleanCustomPreference('hideAccount')
    const hideAccount = hideAccountPref !== null ? hideAccountPref : false

    const isReferralInAccount = useBooleanCustomPreference('showReferralInAccount')
    const isRepairToolInAccount = useBooleanCustomPreference('repairCenterVisible')
    const isSubscriptionHistoryAccount = useBooleanCustomPreference(
        'showSubscriptionHistoryAccount',
    )
    // const logout = useAuthHelper(AuthHelpers.Logout)
    const location = useLocation()
    // const navigate = useNavigation()

    const [mobileNavIndex, setMobileNavIndex] = useState(-1)
    // const [showLoading, setShowLoading] = useState(false)

    // const einstein = useEinstein()

    const {buildUrl} = useMultiSite()

    /**************** Einstein ****************/
    useEffect(() => {
        // einstein.sendViewPage(location.pathname)
    }, [location])

    // const onSignoutClick = async () => {
    //     setShowLoading(true)
    //     await logout.mutateAsync()
    //     navigate('/login')
    // }

    useEffect(() => {
        window.DLG = {
            ...(window.DLG || {}),
            gigya: {brand: BRAND_KEY},
        }
        return () => {
            delete window.DLG
        }
    }, [])

    // If we have customer data and they are not registered, push to login page
    // Using Redirect allows us to store the directed page to location
    // so we can direct users back after they are successfully log in
    if (customerType !== null && !isRegistered && onClient) {
        const path = buildUrl('/login')
        return <Redirect to={{pathname: path, state: {directedFrom: '/account'}}} />
    }

    const getAccountLinkTargetPath = (link) => {
        if (!link) return ''
        return link.name === 'bringAFriend' ? link.path : buildUrl(`/account${link.path || ''}`)
    }

    const normalizePath = (value = '') => value.replace(/\/$/, '') || '/'

    const isAccountLinkActive = (link) => {
        const pathname = normalizePath(location?.pathname)
        const targetPath = normalizePath(getAccountLinkTargetPath(link))
        const accountBasePath = normalizePath(buildUrl('/account'))
        const subscriptionHistoryPath = normalizePath(buildUrl('/account/subscription-history'))

        if (!targetPath) return false

        // Root account entry should only be active on /account.
        if (targetPath === accountBasePath) return pathname === accountBasePath

        if (pathname === targetPath || pathname.startsWith(`${targetPath}/`)) return true

        // Keep subscription history highlighted for its detail/cancel pages.
        if (
            targetPath === subscriptionHistoryPath &&
            (pathname.startsWith(`${normalizePath(buildUrl('/account/subscription-details'))}/`) ||
                pathname.startsWith(`${normalizePath(buildUrl('/account/subscription-cancel'))}/`))
        ) {
            return true
        }

        return false
    }

    return !hideAccount ? (
        <Box bg={'background.neutral.default'}>
            <Flex
                data-testid={
                    isRegistered && isHydrated() ? 'account-page' : 'account-page-skeleton'
                }
                layerStyle="page"
                {...styles.accountContainer}
            >
                <Seo title="My Account" description="Customer Account Page" />
                <Grid
                    w="100%"
                    templateColumns={{base: '1fr', lg: '330px 1fr'}}
                    gap={{base: '0', lg: remCalc(24)}}
                >
                    {/* small screen nav accordion */}
                    <Accordion
                        display={{base: 'block', lg: 'none'}}
                        allowToggle
                        reduceMotion
                        index={mobileNavIndex}
                        onChange={setMobileNavIndex}
                        {...styles.accountAccordion}
                    >
                        <AccordionItem {...styles.accountAccordionItem}>
                            {({isExpanded}) => (
                                <>
                                    <AccordionButton {...styles.accountAccordionButton}>
                                        <Text {...styles.accountAccordionButtonText}>
                                            <FormattedMessage
                                                defaultMessage="My Account"
                                                id="account.accordion.button.my_account"
                                            />
                                        </Text>
                                        {isExpanded ? (
                                            <ChevronUp boxSize="iconsSize.medium" />
                                        ) : (
                                            <ChevronDown boxSize="iconsSize.medium" />
                                        )}
                                    </AccordionButton>
                                    <AccordionPanel {...styles.accountAccordionPanel}>
                                        {accountNavLinks.map((section, sectionIndex) => {
                                            const isLastSection =
                                                sectionIndex === accountNavLinks.length - 1

                                            return (
                                                <Stack
                                                    key={section.title}
                                                    {...styles.accordionSideBarSection}
                                                    sx={{
                                                        borderBottom: isLastSection
                                                            ? 'none'
                                                            : 'small',
                                                        borderStyle: isLastSection
                                                            ? 'none'
                                                            : 'solid',
                                                        borderColor: isLastSection
                                                            ? 'transparent'
                                                            : 'border.light',
                                                        mb: isLastSection ? 0 : remCalc(24),
                                                        pb: isLastSection ? 0 : remCalc(24),
                                                    }}
                                                >
                                                    <Text
                                                        {...styles.accordionInformationTitle}
                                                        sx={{
                                                            ...(isLastSection && {
                                                                fontWeight: 'bold', // Bold title if it's the last one
                                                            }),
                                                        }}
                                                    >
                                                        {formatMessage(
                                                            accountMessages[section.title],
                                                        )}
                                                    </Text>
                                                    <Stack {...styles.accordionGap}>
                                                        {section.links
                                                            .filter((link) => {
                                                                if (
                                                                    link.name === 'bringAFriend' &&
                                                                    !isReferralInAccount
                                                                ) {
                                                                    return false
                                                                }
                                                                if (
                                                                    link.name ===
                                                                        'repairTracking' &&
                                                                    !isRepairToolInAccount
                                                                ) {
                                                                    return false
                                                                }
                                                                if (
                                                                    link.name ===
                                                                        'subscriptionHistory' &&
                                                                    !isSubscriptionHistoryAccount
                                                                ) {
                                                                    return false
                                                                }
                                                                return true
                                                            })
                                                            .map((link) => (
                                                                <Button
                                                                    id={`account-accordion-link-to-${link.name}`}
                                                                    as={Link}
                                                                    to={
                                                                        link.name === 'bringAFriend'
                                                                            ? link.path
                                                                            : `/account${link.path}`
                                                                    }
                                                                    variant="link-button"
                                                                    size="large"
                                                                    width="fit-content"
                                                                    onClick={() => {
                                                                        setMobileNavIndex(-1)
                                                                    }}
                                                                    key={link.name}
                                                                    color={
                                                                        isAccountLinkActive(link)
                                                                            ? 'foreground.neutral.default'
                                                                            : 'foreground.neutral.muted'
                                                                    }
                                                                >
                                                                    {formatMessage(
                                                                        accountMessages[link.name],
                                                                    )}
                                                                </Button>
                                                            ))}
                                                    </Stack>
                                                </Stack>
                                            )
                                        })}
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    </Accordion>

                    {/* large screen nav sidebar */}
                    <Stack {...styles.accountSideBar} data-testid="account-detail-nav">
                        <Flex {...styles.greetingsContainer} data-cs-mask>
                            <Box {...styles.initialsBox}>
                                <Text {...styles.initials}>
                                    {`${customer.firstName?.[0] || ''}${
                                        customer.lastName?.[0] || ''
                                    }`}
                                </Text>
                            </Box>
                            <Stack gap={remCalc(2)}>
                                <Text {...styles.greetings}>
                                    <FormattedMessage
                                        defaultMessage="Greetings"
                                        id="account.heading.greetings"
                                    />
                                </Text>
                                <Flex {...styles.naming}>
                                    <Text {...styles.name}>{customer.firstName}</Text>
                                    <Text {...styles.name}>{customer.lastName}</Text>
                                </Flex>
                            </Stack>
                        </Flex>
                        {accountNavLinks.map((section) => (
                            <Stack key={section.title} {...styles.sideBarSection}>
                                <Text {...styles.personalInformationTitle}>
                                    {formatMessage(accountMessages[section.title])}
                                </Text>
                                <Stack {...styles.sideBarGap}>
                                    {section.links
                                        .filter((link) => {
                                            if (
                                                link.name === 'bringAFriend' &&
                                                !isReferralInAccount
                                            ) {
                                                return false
                                            }
                                            if (
                                                link.name === 'repairTracking' &&
                                                !isRepairToolInAccount
                                            ) {
                                                return false
                                            }
                                            if (
                                                link.name === 'subscriptionHistory' &&
                                                !isSubscriptionHistoryAccount
                                            ) {
                                                return false
                                            }
                                            return true
                                        })
                                        .map((link) => (
                                            <Button
                                                as={Link}
                                                variant="link-button"
                                                size="large"
                                                width="fit-content"
                                                to={
                                                    link.name === 'bringAFriend'
                                                        ? link.path
                                                        : `/account${link.path}`
                                                }
                                                key={link.name}
                                                color={
                                                    isAccountLinkActive(link)
                                                        ? 'foreground.neutral.default'
                                                        : 'foreground.neutral.muted'
                                                }
                                            >
                                                {formatMessage(accountMessages[link.name])}
                                            </Button>
                                        ))}
                                </Stack>
                                {section.title === 'personalInformation' && (
                                    <Box id="mcp-account-container"></Box>
                                )}
                            </Stack>
                        ))}
                    </Stack>

                    <Switch>
                        <Route exact path={path}>
                            <AccountDetail />
                        </Route>
                        <Route exact path={`${path}/wishlist`}>
                            <AccountWishlist />
                        </Route>
                        <Route exact path={`${path}/addresses`}>
                            <AccountAddresses />
                        </Route>
                        <Route exact path={`${path}/payment-methods`}>
                            <PaymentMethods />
                        </Route>
                        <Route path={`${path}/orders`}>
                            <Orders />
                        </Route>
                        <Route path={`${path}/marketing-preferences`}>
                            <MarketingPreferences />
                        </Route>
                        <Route path={`${path}/registered-products`}>
                            <RegisteredProducts customer={customer} />
                        </Route>
                        <Route path={`${path}/repair-tracking-tool`}>
                            <RepairTracking />
                        </Route>
                        <Route path={`${path}/subscription-history`}>
                            <SubscriptionList customer={customer} />
                        </Route>
                        <Route path={`${path}/subscription-details/:subscriptionId`}>
                            <SubscriptionDetails />
                        </Route>
                        <Route path={`${path}/subscription-cancel/:subscriptionId`}>
                            <SubscriptionCancel />
                        </Route>
                    </Switch>
                </Grid>
            </Flex>
        </Box>
    ) : (
        <GenericHtmlPage />
    )
}

Account.getTemplateName = () => 'account'

export default Account
